/**
 * Find the correct scroll container for the document.
 * Checks .doc-root first if it's scrollable, otherwise falls back to other containers.
 */
export function findScrollContainer(
  docRoot: HTMLElement | null
): HTMLElement | null {
  // Check .doc-root first if it's scrollable
  const docRootScroller =
    docRoot &&
    getComputedStyle(docRoot).overflowY !== "visible" &&
    docRoot.scrollHeight > docRoot.clientHeight
      ? docRoot
      : null;

  return (
    docRootScroller ||
    document.querySelector<HTMLElement>('[data-content-scroll="true"]') ||
    (document.scrollingElement as HTMLElement) ||
    document.documentElement
  );
}

/** The nearest ancestor of `node` that scrolls vertically, else the page. */
function nearestScroller(node: Node): Element {
  for (let el = node.parentElement; el; el = el.parentElement) {
    const { overflowY } = getComputedStyle(el);
    if (
      (overflowY === "auto" || overflowY === "scroll") &&
      el.scrollHeight > el.clientHeight
    ) {
      return el;
    }
  }
  return document.scrollingElement ?? document.documentElement;
}

/**
 * Scrolls `range` to the vertical centre of its nearest scrolling ancestor,
 * as `scrollIntoView({ block: "center" })` does for an element. Takes a live
 * `Range`, since `StaticRange` has no geometry (ADR 0001).
 */
export function scrollRangeIntoView(
  range: Range,
  behavior: ScrollBehavior
): void {
  const scroller = nearestScroller(range.startContainer);
  const isPage =
    scroller === document.scrollingElement ||
    scroller === document.documentElement;
  const viewTop = isPage ? 0 : scroller.getBoundingClientRect().top;
  const viewHeight = isPage ? window.innerHeight : scroller.clientHeight;
  const rect = range.getBoundingClientRect();
  const top =
    scroller.scrollTop + (rect.top - viewTop) - (viewHeight - rect.height) / 2;
  scroller.scrollTo({ top: Math.max(0, top), behavior });
}

/**
 * Calculate scroll offset so target content clears sticky document chrome.
 * When scrolling inside `[data-content-scroll]`, the app bar is already outside
 * the scroll viewport — offset by the sticky document toolbar height instead.
 */
export function calculateScrollOffset(scroller?: HTMLElement | null): number {
  const padding = 12;

  if (scroller?.hasAttribute("data-content-scroll")) {
    const stickyToolbar =
      scroller.querySelector<HTMLElement>(".sticky.top-0.border-b") ??
      scroller.querySelector<HTMLElement>(".sticky.top-0");
    const toolbarHeight = stickyToolbar?.getBoundingClientRect().height ?? 0;
    if (toolbarHeight > 0) {
      return toolbarHeight + padding;
    }
  }

  const appbarHeightStr = getComputedStyle(document.documentElement)
    .getPropertyValue("--appbar-height")
    .trim();

  let appbarHeight = 48; // fallback to 3rem = 48px
  if (appbarHeightStr) {
    const numValue = parseFloat(appbarHeightStr);
    if (appbarHeightStr.endsWith("rem")) {
      appbarHeight = numValue * 16;
    } else if (appbarHeightStr.endsWith("px")) {
      appbarHeight = numValue;
    } else {
      appbarHeight = numValue * 16;
    }
  }
  return appbarHeight + padding;
}
